import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, Save } from "lucide-react";
import { RichEditor } from "@/components/admin/RichEditor";

// Page slugs and human labels
const PAGES: Record<string, { label: string; sections: { key: string; label: string; multiline?: boolean }[] }> = {
  index: {
    label: "Etusivu",
    sections: [
      { key: "hero_nickname", label: "Lempinimi (esim. Mörrikkä)" },
      { key: "hero_intro", label: "Hero – esittelyteksti", multiline: true },
      { key: "hero_chip_1", label: "Hero – nappi 1" },
      { key: "hero_chip_2", label: "Hero – nappi 2" },
      { key: "hero_chip_3", label: "Hero – nappi 3" },
      { key: "hero_cta", label: "Hero – kutsu-painikkeen teksti" },
      { key: "vision_title", label: "Visio – otsikko" },
      { key: "vision_card_1_title", label: "Visio kortti 1 – otsikko" },
      { key: "vision_card_1_desc", label: "Visio kortti 1 – kuvaus" },
      { key: "vision_card_2_title", label: "Visio kortti 2 – otsikko" },
      { key: "vision_card_2_desc", label: "Visio kortti 2 – kuvaus" },
      { key: "vision_card_3_title", label: "Visio kortti 3 – otsikko" },
      { key: "vision_card_3_desc", label: "Visio kortti 3 – kuvaus" },
      { key: "contact_title", label: "Tutustu-osio – otsikko" },
      { key: "contact_card_1_title", label: "Tutustu kortti 1 – otsikko" },
      { key: "contact_card_1_desc", label: "Tutustu kortti 1 – kuvaus" },
      { key: "contact_card_2_title", label: "Tutustu kortti 2 – otsikko" },
      { key: "contact_card_2_desc", label: "Tutustu kortti 2 – kuvaus" },
      { key: "contact_card_3_title", label: "Tutustu kortti 3 – otsikko" },
      { key: "contact_card_3_desc", label: "Tutustu kortti 3 – kuvaus" },
    ],
  },
  about: {
    label: "Tietoa",
    sections: [
      { key: "hero_badge", label: "Yläotsikko" },
      { key: "hero_title", label: "Pääotsikko" },
      { key: "body_paragraph_1", label: "Kappale 1", multiline: true },
      { key: "body_paragraph_2", label: "Kappale 2", multiline: true },
      { key: "body_nickname_line", label: "Lempinimi-rivi", multiline: true },
      { key: "focus_text", label: "Tärkeitä asioita -teksti", multiline: true },
    ],
  },
  experience: {
    label: "Kokemus",
    sections: [
      { key: "hero_badge", label: "Yläotsikko" },
      { key: "hero_title", label: "Pääotsikko" },
    ],
  },
  contact: {
    label: "Yhteystiedot",
    sections: [
      { key: "hero_title", label: "Pääotsikko" },
      { key: "email", label: "Sähköpostiosoite" },
      { key: "social_title", label: "Some-osion otsikko" },
      { key: "org_name", label: "Organisaation nimi" },
      { key: "form_title", label: "Lomakkeen otsikko" },
    ],
  },
  blog: {
    label: "Ajankohtaista",
    sections: [
      { key: "hero_badge", label: "Yläotsikko" },
      { key: "hero_title", label: "Pääotsikko" },
      { key: "placeholder_title", label: "Tyhjän tilan otsikko" },
      { key: "placeholder_subtitle", label: "Tyhjän tilan alaotsikko" },
    ],
  },
};

export const PageList = () => (
  <div>
    <h2 className="mb-6 font-display text-2xl font-semibold">Sivut</h2>
    <div className="grid gap-3 sm:grid-cols-2">
      {Object.entries(PAGES).map(([slug, { label }]) => (
        <Link
          key={slug}
          to={`/admin/sivut/${slug}`}
          className="rounded-xl border bg-card p-4 shadow-sm transition hover:bg-muted"
        >
          <p className="font-semibold">{label}</p>
          <p className="text-sm text-muted-foreground">/{slug === "index" ? "" : slug}</p>
        </Link>
      ))}
    </div>
  </div>
);

export const PageEditor = () => {
  const { slug } = useParams<{ slug: string }>();
  const cfg = slug ? PAGES[slug] : undefined;
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin_page_content", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("section,value")
        .eq("page", slug!);
      if (error) throw error;
      const map: Record<string, string> = {};
      data?.forEach((r) => (map[r.section] = r.value));
      return map;
    },
    enabled: !!slug,
  });

  useEffect(() => {
    if (data) setValues(data);
  }, [data]);

  if (!cfg) return <Navigate to="/admin/sivut" replace />;

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const rows = cfg.sections.map((s) => ({
        page: slug,
        section: s.key,
        value: values[s.key] ?? "",
      }));
      const { error } = await supabase
        .from("page_content")
        .upsert(rows, { onConflict: "page,section" });
      if (error) throw error;
      toast.success("Tallennettu! Muutokset näkyvät heti sivustolla.");
      qc.invalidateQueries({ queryKey: ["page_content", slug] });
      qc.invalidateQueries({ queryKey: ["admin_page_content", slug] });
    } catch (e: any) {
      toast.error(e.message || "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Link to="/admin/sivut" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="mr-1 h-4 w-4" /> Takaisin sivulistaan
      </Link>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">{cfg.label}</h2>
        <Button onClick={handleSave} disabled={saving || isLoading}>
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Tallennetaan..." : "Tallenna"}
        </Button>
      </div>
      <p className="mb-6 rounded-md bg-muted p-3 text-sm text-muted-foreground">
        Pidemmissä kentissä on rikastettu editori: lihavointi, kursivointi, listat, väliotsikot, sitaatit, linkit ja 3 valittavaa fonttia. Sivuston ilme pysyy yhtenäisenä – käytettävissä ovat vain hyväksytyt tyylit.
      </p>
      {isLoading ? (
        <p className="text-muted-foreground">Ladataan...</p>
      ) : (
        <div className="space-y-4 rounded-2xl border bg-card p-6">
          {cfg.sections.map((s) => (
            <div key={s.key}>
              <Label htmlFor={s.key}>{s.label}</Label>
              {s.multiline ? (
                <RichEditor
                  value={values[s.key] ?? ""}
                  onChange={(html) => setValues({ ...values, [s.key]: html })}
                />
              ) : (
                <Input
                  id={s.key}
                  value={values[s.key] ?? ""}
                  onChange={(e) => setValues({ ...values, [s.key]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
